"use client";

import { useState, useEffect, useCallback, useRef } from "react";
import Link from "next/link";
import { supabase } from "../../app/lib/supabase/client";
import { LEGAL_DOCS_VERSION, LEGAL_PATHS } from "../../app/lib/legalConstants";
import { ensureProfileForUser } from "../../app/lib/auth/ensureProfileForUser";
import { getPublicSiteOriginClient } from "../../app/lib/publicSiteUrl";
import { useRouter } from "next/navigation";

interface AuthPopupProps {
  isOpen: boolean;
  onClose: () => void;
  /** Сохранённый в Header `next` до очистки query (редирект после входа с защищённого URL) */
  postLoginRedirect?: string | null;
  /** Какую вкладку показать при открытии (после открытия можно переключить вручную) */
  initialMode?: "signin" | "signup";
}

type AuthMode = "signin" | "signup" | "forgot";

/** After email sign-up when Supabase sends a confirmation link, show a simple screen before auto-close. */
const SIGNUP_EMAIL_CONFIRM_MS = 12_000;

export default function AuthPopup({ isOpen, onClose, postLoginRedirect = null, initialMode = "signin" }: AuthPopupProps) {
  const router = useRouter();
  const authSuccessLock = useRef(false);
  const signupEmailCloseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [signupAwaitingEmailConfirm, setSignupAwaitingEmailConfirm] = useState(false);
  const [mode, setMode] = useState<AuthMode>(initialMode);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [policyAccepted, setPolicyAccepted] = useState(false);

  useEffect(() => {
    if (mode !== "signup") {
      setPolicyAccepted(false);
    }
  }, [mode]);

  useEffect(() => {
    if (!isOpen) return;
    setMode(initialMode);
    setError(null);
    setSuccessMessage(null);
    setSignupAwaitingEmailConfirm(false);
  }, [isOpen, initialMode]);

  useEffect(() => {
    if (!isOpen) {
      if (signupEmailCloseTimerRef.current) {
        clearTimeout(signupEmailCloseTimerRef.current);
        signupEmailCloseTimerRef.current = null;
      }
      setSignupAwaitingEmailConfirm(false);
    }
  }, [isOpen]);

  useEffect(() => {
    return () => {
      if (signupEmailCloseTimerRef.current) {
        clearTimeout(signupEmailCloseTimerRef.current);
        signupEmailCloseTimerRef.current = null;
      }
    };
  }, []);

  // Функция для сбора данных об источнике перехода
  const getRefSource = useCallback(() => {
    // Безопасное чтение из localStorage (на случай ошибки или SSR)
    let firstPage = "";
    let firstReferrer = "";
    try {
      firstPage = localStorage.getItem("first_page") || "";
      firstReferrer = localStorage.getItem("first_referrer") || "";
    } catch (e) {
      if (process.env.NODE_ENV !== "production") {
        console.warn("localStorage недоступен", e);
      }
    }

    // Если по какой-то причине first_page нет, заполняем прямо сейчас
    if (!firstPage && typeof window !== "undefined") {
      firstPage = window.location.href;
      firstReferrer = document.referrer || "";
      try {
        localStorage.setItem("first_page", firstPage);
        localStorage.setItem("first_referrer", firstReferrer);
      } catch (e) {}
    }

    // Парсим UTM-метки из текущего URL
    const urlParams = new URLSearchParams(window.location.search);
    const utmSource = urlParams.get("utm_source");
    const utmCampaign = urlParams.get("utm_campaign");
    const utmMedium = urlParams.get("utm_medium");
    const utmContent = urlParams.get("utm_content");
    const utmTerm = urlParams.get("utm_term");

    // Текущая страница, на которой происходит регистрация
    const registrationPage = window.location.pathname;

    // Формируем объект ref_source
    const refSource: Record<string, any> = {
      first_page: firstPage,
      first_referrer: firstReferrer,
      registration_page: registrationPage,
    };

    if (utmSource) refSource.utm_source = utmSource;
    if (utmCampaign) refSource.utm_campaign = utmCampaign;
    if (utmMedium) refSource.utm_medium = utmMedium;
    if (utmContent) refSource.utm_content = utmContent;
    if (utmTerm) refSource.utm_term = utmTerm;

    return refSource;
  }, []);

  const ensureProfile = useCallback(async () => {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) return;
    await ensureProfileForUser(supabase, user);
  }, []);

  const handleAuthSuccess = useCallback(async () => {
    if (authSuccessLock.current) return;
    authSuccessLock.current = true;
    try {
      await ensureProfile();
      onClose();
      const target = postLoginRedirect && postLoginRedirect.startsWith("/") ? postLoginRedirect : "/dashboard/resume";
      router.replace(target);
      router.refresh();
    } catch (err) {
      console.error("Profile creation error:", err);
      setError("Failed to create profile. Please try again.");
    } finally {
      authSuccessLock.current = false;
    }
  }, [onClose, router, ensureProfile, postLoginRedirect]);

  useEffect(() => {
    if (!isOpen) return;
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === "SIGNED_IN" && session) {
        handleAuthSuccess();
      }
    });
    return () => subscription.unsubscribe();
  }, [isOpen, handleAuthSuccess]);

  const handleGoogleSignIn = async () => {
    setError(null);
    setSuccessMessage(null);
    setLoading(true);
    try {
      const nextPath = postLoginRedirect && postLoginRedirect.startsWith("/") ? postLoginRedirect : "/dashboard/resume";
      window.location.assign(`/api/auth/oauth-google?next=${encodeURIComponent(nextPath)}`);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Google sign-in failed";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccessMessage(null);

    const formData = new FormData(e.currentTarget);
    const email = formData.get("email") as string;
    const password = formData.get("password") as string;
    const fullName = formData.get("fullName") as string | null;

    try {
      if (mode === "forgot") {
        const origin = getPublicSiteOriginClient();
        if (!origin) {
          setLoading(false);
          setError("Site URL is not configured. Set NEXT_PUBLIC_SITE_URL for production.");
          return;
        }
        const redirectTo = `${origin}/auth/update-password`;
        const { error: resetError } = await supabase.auth.resetPasswordForEmail(email.trim(), {
          redirectTo,
        });
        if (resetError) {
          throw resetError;
        }
        setSuccessMessage("If an account exists for this address, we sent a message with a link to reset your password.");
      } else if (mode === "signup") {
        if (!policyAccepted) {
          setLoading(false);
          setError("Please accept the Terms of Use and Privacy Policy to create an account.");
          return;
        }
        // Собираем данные об источнике перехода
        const refSource = getRefSource();

        const res = await fetch("/api/auth/email-signup", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            email: email.trim(),
            password,
            metadata: {
              full_name: fullName || undefined,
              ref_source: refSource,
              legal_accepted_at: new Date().toISOString(),
              legal_docs_version: LEGAL_DOCS_VERSION,
              marketing_opt_in: false,
            },
          }),
        });

        const payload = (await res.json().catch(() => ({}))) as {
          error?: string;
          session?: boolean;
          userId?: string | null;
        };

        if (!res.ok) {
          const msg = typeof payload.error === "string" ? payload.error : "Sign up failed";
          if (msg.toLowerCase().includes("already registered") || msg.toLowerCase().includes("user already registered")) {
            throw new Error("A user with this email is already registered");
          }
          if (msg.includes("Password")) {
            throw new Error("Password must be at least 6 characters long");
          }
          throw new Error(msg);
        }

        await supabase.auth.getSession();

        if (payload.session) {
          await handleAuthSuccess();
        } else if (payload.userId) {
          await ensureProfile();
          setError(null);
          setSuccessMessage("We sent a confirmation link to your email. Open it to finish signing up - then you can close this window.");
          setSignupAwaitingEmailConfirm(true);
          if (signupEmailCloseTimerRef.current) {
            clearTimeout(signupEmailCloseTimerRef.current);
          }
          signupEmailCloseTimerRef.current = setTimeout(() => {
            signupEmailCloseTimerRef.current = null;
            setSignupAwaitingEmailConfirm(false);
            setSuccessMessage(null);
            onClose();
            router.refresh();
          }, SIGNUP_EMAIL_CONFIRM_MS);
        }
      } else {
        const { data, error: signInError } = await supabase.auth.signInWithPassword({
          email,
          password,
        });

        if (signInError) {
          if (signInError.message.includes("Invalid login credentials")) {
            throw new Error("Invalid email or password");
          }
          if (signInError.message.includes("Email not confirmed")) {
            throw new Error("Email is not confirmed. Please check your inbox.");
          }
          throw signInError;
        }

        if (data.user && data.session) {
          await handleAuthSuccess();
        }
      }
    } catch (err: any) {
      setError(err.message || "An error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-start justify-center overflow-y-auto bg-black/50 dark:bg-black/70 p-4 py-8 sm:items-center sm:py-4"
      onClick={signupAwaitingEmailConfirm ? undefined : onClose}
    >
      <div
        className="my-auto flex w-full max-w-md max-h-[90dvh] flex-col overflow-hidden rounded-2xl bg-white text-slate-900 shadow-2xl dark:bg-slate-900 dark:text-slate-100"
        style={{ colorScheme: "light dark" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex shrink-0 items-center justify-between border-b border-slate-200 px-4 py-3 dark:border-slate-800 sm:px-6 sm:py-4">
          <h2 className="text-lg font-bold text-slate-900 dark:text-slate-100 sm:text-xl">
            {signupAwaitingEmailConfirm ? "Check your email" : mode === "signin" ? "Sign In" : mode === "signup" ? "Register" : "Reset password"}
          </h2>
          <button
            onClick={onClose}
            className="text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200 transition-colors cursor-pointer"
            aria-label="Close"
          >
            <span className="material-icons-outlined">close</span>
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 py-4 text-slate-900 dark:text-slate-100 sm:px-6 sm:py-5">
          {signupAwaitingEmailConfirm ? (
            <div className="flex flex-col items-center gap-5 py-6 text-center sm:py-8">
              <div className="flex size-14 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300" aria-hidden>
                <span className="material-icons-outlined text-3xl">mark_email_read</span>
              </div>
              <p className="max-w-sm text-sm leading-relaxed text-slate-700 dark:text-slate-200">{successMessage}</p>
              <p className="text-xs text-slate-500 dark:text-slate-400">This window closes automatically in a few seconds, or use the close button above when you are done.</p>
              <button
                type="button"
                onClick={() => {
                  if (signupEmailCloseTimerRef.current) {
                    clearTimeout(signupEmailCloseTimerRef.current);
                    signupEmailCloseTimerRef.current = null;
                  }
                  setSignupAwaitingEmailConfirm(false);
                  setSuccessMessage(null);
                  onClose();
                  router.refresh();
                }}
                className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-800 transition-colors hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700 cursor-pointer"
              >
                Close now
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {mode !== "forgot" && (
                <div className="flex gap-2 bg-slate-100 dark:bg-slate-800 p-1 rounded-lg">
                  <button
                    type="button"
                    onClick={() => {
                      if (signupEmailCloseTimerRef.current) {
                        clearTimeout(signupEmailCloseTimerRef.current);
                        signupEmailCloseTimerRef.current = null;
                      }
                      setSignupAwaitingEmailConfirm(false);
                      setMode("signin");
                      setError(null);
                      setSuccessMessage(null);
                    }}
                    className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-colors cursor-pointer ${
                      mode === "signin" ? "bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow-sm" : "text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white"
                    }`}
                  >
                    Sign in
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      if (signupEmailCloseTimerRef.current) {
                        clearTimeout(signupEmailCloseTimerRef.current);
                        signupEmailCloseTimerRef.current = null;
                      }
                      setSignupAwaitingEmailConfirm(false);
                      setMode("signup");
                      setError(null);
                      setSuccessMessage(null);
                    }}
                    className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-colors cursor-pointer ${
                      mode === "signup" ? "bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow-sm" : "text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white"
                    }`}
                  >
                    Register
                  </button>
                </div>
              )}

              {mode === "signup" && (
                <p className="rounded-md border border-emerald-100 bg-emerald-50/90 px-2.5 py-2 text-xs leading-snug text-slate-700 dark:border-emerald-900/40 dark:bg-emerald-950/30 dark:text-slate-200">
                  After sign-up: short setup → free <strong className="text-slate-800 dark:text-slate-100">interview prep skills</strong> (Foundation + Quant, IB, and Data tracks).
                </p>
              )}

              {mode === "forgot" && (
                <button
                  type="button"
                  onClick={() => {
                    if (signupEmailCloseTimerRef.current) {
                      clearTimeout(signupEmailCloseTimerRef.current);
                      signupEmailCloseTimerRef.current = null;
                    }
                    setSignupAwaitingEmailConfirm(false);
                    setMode("signin");
                    setError(null);
                    setSuccessMessage(null);
                  }}
                  className="text-sm text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white cursor-pointer"
                >
                  ← Back to sign in
                </button>
              )}

              {successMessage && (
                <div className="px-4 py-3 rounded-lg text-sm bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-green-700 dark:text-green-300">{successMessage}</div>
              )}

              {error && (
                <div
                  className={`px-4 py-3 rounded-lg text-sm ${
                    error.includes("успешна")
                      ? "bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-green-700 dark:text-green-300"
                      : "bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300"
                  }`}
                >
                  {error}
                </div>
              )}

              {mode !== "forgot" && (
                <>
                  {mode === "signup" && (
                    <label className="flex cursor-pointer items-start gap-2 rounded-md border border-slate-200 bg-slate-50 px-2.5 py-2 text-xs text-slate-700 dark:border-slate-700 dark:bg-slate-800/50 dark:text-slate-300">
                      <input type="checkbox" checked={policyAccepted} onChange={(e) => setPolicyAccepted(e.target.checked)} className="mt-0.5 shrink-0 rounded border-slate-300" />
                      <span className="leading-snug">
                        I agree to the{" "}
                        <Link href={LEGAL_PATHS.terms} target="_blank" rel="noopener noreferrer" className="font-medium text-emerald-700 underline dark:text-emerald-400">
                          Terms of Use
                        </Link>{" "}
                        and{" "}
                        <Link href={LEGAL_PATHS.privacy} target="_blank" rel="noopener noreferrer" className="font-medium text-emerald-700 underline dark:text-emerald-400">
                          Privacy Policy
                        </Link>
                        .
                      </span>
                    </label>
                  )}
                  <button
                    type="button"
                    onClick={() => void handleGoogleSignIn()}
                    disabled={loading || (mode === "signup" && !policyAccepted)}
                    title={mode === "signup" && !policyAccepted ? "Accept the Terms of Use and Privacy Policy above to continue" : undefined}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2.5 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 text-sm font-medium hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-50 transition-colors cursor-pointer"
                  >
                    <svg className="size-5 shrink-0" viewBox="0 0 24 24" aria-hidden>
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                    </svg>
                    Continue with Google
                  </button>
                  <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                      <span className="w-full border-t border-slate-200 dark:border-slate-700" />
                    </div>
                    <div className="relative flex justify-center text-xs uppercase">
                      <span className="bg-white dark:bg-slate-900 px-2 text-slate-500 dark:text-slate-400">or</span>
                    </div>
                  </div>
                </>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                {mode === "signup" && (
                  <div>
                    <label htmlFor="auth-fullName" className="block text-sm font-medium mb-2 text-slate-800 dark:text-slate-200">
                      Name (optional)
                    </label>
                    <input
                      id="auth-fullName"
                      name="fullName"
                      type="text"
                      disabled={loading}
                      className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 placeholder:text-slate-400 dark:placeholder:text-slate-500 focus:ring-2 focus:ring-primary focus:border-transparent disabled:opacity-50"
                      placeholder="Your name"
                    />
                  </div>
                )}

                <div>
                  <label htmlFor="auth-email" className="block text-sm font-medium mb-2 text-slate-800 dark:text-slate-200">
                    Email *
                  </label>
                  <input
                    id="auth-email"
                    name="email"
                    type="email"
                    required
                    disabled={loading}
                    className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 placeholder:text-slate-400 dark:placeholder:text-slate-500 focus:ring-2 focus:ring-primary focus:border-transparent disabled:opacity-50"
                    placeholder="your@email.com"
                  />
                </div>

                {mode !== "forgot" && (
                  <div>
                    <div className="flex items-center justify-between gap-2 mb-2">
                      <label htmlFor="auth-password" className="block text-sm font-medium text-slate-800 dark:text-slate-200">
                        Password *
                      </label>
                      {mode === "signin" && (
                        <button
                          type="button"
                          onClick={() => {
                            if (signupEmailCloseTimerRef.current) {
                              clearTimeout(signupEmailCloseTimerRef.current);
                              signupEmailCloseTimerRef.current = null;
                            }
                            setSignupAwaitingEmailConfirm(false);
                            setMode("forgot");
                            setError(null);
                            setSuccessMessage(null);
                          }}
                          className="text-xs font-medium text-emerald-700 hover:underline cursor-pointer dark:text-emerald-400"
                        >
                          Forgot password?
                        </button>
                      )}
                    </div>
                    <input
                      id="auth-password"
                      name="password"
                      type="password"
                      required
                      minLength={6}
                      disabled={loading}
                      className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 placeholder:text-slate-400 dark:placeholder:text-slate-500 focus:ring-2 focus:ring-primary focus:border-transparent disabled:opacity-50"
                      placeholder={mode === "signup" ? "At least 6 characters" : "••••••••"}
                    />
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading || (mode === "signup" && !policyAccepted)}
                  className="w-full bg-slate-100 text-slate-900 py-3 rounded-lg font-semibold hover:bg-slate-200 dark:bg-slate-100 dark:text-slate-900 dark:hover:bg-white disabled:opacity-50 transition-opacity cursor-pointer"
                >
                  {loading
                    ? mode === "forgot"
                      ? "Sending…"
                      : mode === "signup"
                      ? "Registering..."
                      : "Signing in..."
                    : mode === "forgot"
                    ? "Send reset link"
                    : mode === "signup"
                    ? "Register"
                    : "Sign In"}
                </button>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
